import {
    AUTOMATION_OPERATOR_AND,
    AUTOMATION_OPERATOR_OR,
    createConditionRow,
    createConditionGroup,
} from './conditionFormHelpers';

function resolveConditionIdFromFilter(filter, configs) {
    const defs = configs?.conditions;
    if (!defs || !filter) {
        return null;
    }
    for (const [conditionId, def] of Object.entries(defs)) {
        if (def?.filters && Object.prototype.hasOwnProperty.call(def.filters, filter)) {
            return conditionId;
        }
    }
    return null;
}

/**
 * Turn the backend-owned editor payload (RND-1619) into editor form state. The payload already exposes the
 * search plan pre-extracted as `plan` / `values` and the action config as `action.value`, so no raw rule
 * parsing happens here. Conditions are grouped (RND-1378): the plan is the Advanced-Search `{op, items}`
 * format where each top-level item is a group of leaf conditions.
 */
export function deserializeRecruitmentAutomationRule(ruleDetail, configs) {
    const event = ruleDetail?.event ?? null;
    const cron = Array.isArray(ruleDetail?.cron)
        ? ruleDetail.cron.map((row) => (Array.isArray(row) ? [...row] : row))
        : [];

    const actionValue =
        ruleDetail?.action?.value && typeof ruleDetail.action.value === 'object'
            ? { ...ruleDetail.action.value }
            : {};

    const plan = ruleDetail?.plan || { op: AUTOMATION_OPERATOR_AND, items: [] };
    const values = ruleDetail?.values || {};
    const normalizeOp = (op) => (op === AUTOMATION_OPERATOR_OR ? AUTOMATION_OPERATOR_OR : AUTOMATION_OPERATOR_AND);
    const topOperator = normalizeOp(plan.op);

    const resolveValue = (vid) => {
        const rawVal = values[String(vid)] !== undefined ? values[String(vid)] : values[vid];
        return Array.isArray(rawVal) ? rawVal : rawVal != null ? [rawVal] : [];
    };

    // Map every group in the plan (not just the first) so grouped conditions survive a round-trip.
    // A legacy flat rule is a single-group plan, so it deserialises to one group unchanged.
    const groupNodes = Array.isArray(plan.items) ? plan.items : [];
    const groups = [];
    for (const groupNode of groupNodes) {
        const leafItems = Array.isArray(groupNode?.items) ? groupNode.items : [];
        const conditions = [];
        for (const planItem of leafItems) {
            if (!planItem?.filter) {
                continue;
            }
            const conditionId = resolveConditionIdFromFilter(planItem.filter, configs);
            if (!conditionId) {
                continue;
            }
            conditions.push({
                id: conditionId,
                ...createConditionRow({ filter: planItem.filter, value: resolveValue(planItem.id) }),
            });
        }
        if (conditions.length > 0) {
            groups.push(createConditionGroup({ op: normalizeOp(groupNode?.op), conditions }));
        }
    }

    return {
        event,
        cron,
        groups,
        topOperator,
        actionValue,
    };
}
